import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { COLORS, fontFamilyAll } from "../assets/commonComponents/COLORS";
import { googleMapLink } from "../assets/commonComponents/data";
import ToastMessages from "../assets/commonComponents/ToastMessages";
import { submitFormAPI } from "../Util/UtilApi";

// ---------------------------------------------------------------------------
// Country codes (used for any field of type "tel")
// ---------------------------------------------------------------------------
const countryCodes = [
  { code: "1", name: "United States - US" },
  { code: "7", name: "Russia - RU" },
  { code: "20", name: "Egypt - EG" },
  { code: "27", name: "South Africa - ZA" },
  { code: "30", name: "Greece - GR" },
  { code: "31", name: "Netherlands - NL" },
  { code: "32", name: "Belgium - BE" },
  { code: "33", name: "France - FR" },
  { code: "34", name: "Spain - ES" },
  { code: "36", name: "Hungary - HU" },
  { code: "39", name: "Italy - IT" },
  { code: "40", name: "Romania - RO" },
  { code: "41", name: "Switzerland - CH" },
  { code: "43", name: "Austria - AT" },
  { code: "44", name: "United Kingdom - GB" },
  { code: "45", name: "Denmark - DK" },
  { code: "46", name: "Sweden - SE" },
  { code: "47", name: "Norway - NO" },
  { code: "48", name: "Poland - PL" },
  { code: "49", name: "Germany - DE" },
  { code: "51", name: "Peru - PE" },
  { code: "52", name: "Mexico - MX" },
  { code: "53", name: "Cuba - CU" },
  { code: "54", name: "Argentina - AR" },
  { code: "55", name: "Brazil - BR" },
  { code: "56", name: "Chile - CL" },
  { code: "57", name: "Colombia - CO" },
  { code: "58", name: "Venezuela - VE" },
  { code: "60", name: "Malaysia - MY" },
  { code: "61", name: "Australia - AU" },
  { code: "62", name: "Indonesia - ID" },
  { code: "63", name: "Philippines - PH" },
  { code: "64", name: "New Zealand - NZ" },
  { code: "65", name: "Singapore - SG" },
  { code: "66", name: "Thailand - TH" },
  { code: "81", name: "Japan - JP" },
  { code: "82", name: "South Korea - KR" },
  { code: "84", name: "Vietnam - VN" },
  { code: "86", name: "China - CN" },
  { code: "90", name: "Turkey - TR" },
  { code: "91", name: "India - IN" },
  { code: "92", name: "Pakistan - PK" },
  { code: "93", name: "Afghanistan - AF" },
  { code: "94", name: "Sri Lanka - LK" },
  { code: "95", name: "Myanmar - MM" },
  { code: "98", name: "Iran - IR" },
  { code: "211", name: "South Sudan - SS" },
  { code: "212", name: "Morocco - MA" },
  { code: "213", name: "Algeria - DZ" },
  { code: "216", name: "Tunisia - TN" },
  { code: "218", name: "Libya - LY" },
  { code: "220", name: "Gambia - GM" },
  { code: "221", name: "Senegal - SN" },
  { code: "222", name: "Mauritania - MR" },
  { code: "223", name: "Mali - ML" },
  { code: "224", name: "Guinea - GN" },
  { code: "225", name: "Ivory Coast - CI" },
  { code: "226", name: "Burkina Faso - BF" },
  { code: "227", name: "Niger - NE" },
  { code: "228", name: "Togo - TG" },
  { code: "229", name: "Benin - BJ" },
  { code: "230", name: "Mauritius - MU" },
  { code: "231", name: "Liberia - LR" },
  { code: "232", name: "Sierra Leone - SL" },
  { code: "233", name: "Ghana - GH" },
  { code: "234", name: "Nigeria - NG" },
  { code: "235", name: "Chad - TD" },
  { code: "236", name: "Central African Republic - CF" },
  { code: "237", name: "Cameroon - CM" },
  { code: "238", name: "Cape Verde - CV" },
  { code: "239", name: "Sao Tome and Principe - ST" },
  { code: "240", name: "Equatorial Guinea - GQ" },
  { code: "241", name: "Gabon - GA" },
  { code: "242", name: "Congo - Brazzaville - CG" },
  { code: "243", name: "Congo - Kinshasa - CD" },
  { code: "244", name: "Angola - AO" },
  { code: "245", name: "Guinea-Bissau - GW" },
  { code: "246", name: "British Indian Ocean Territory - IO" },
  { code: "248", name: "Seychelles - SC" },
  { code: "249", name: "Sudan - SD" },
  { code: "250", name: "Rwanda - RW" },
  { code: "251", name: "Ethiopia - ET" },
  { code: "252", name: "Somalia - SO" },
  { code: "253", name: "Djibouti - DJ" },
  { code: "254", name: "Kenya - KE" },
  { code: "255", name: "Tanzania - TZ" },
];

// ---------------------------------------------------------------------------
// Field styles (unchanged look/feel, shared by every input type)
// ---------------------------------------------------------------------------
const FieldStyles = (error) => ({
  fontFamily: `${fontFamilyAll} !important`,
  "& .MuiOutlinedInput-root": {
    fontFamily: `${fontFamilyAll} !important`,
    borderRadius: "12px",
    backgroundColor: "#fafafa",
    "& fieldset": {
      borderColor: error ? "red !important" : `${COLORS.secondaryColor} !important`,
    },
    "&:hover fieldset": {
      borderColor: error ? "red !important" : `${COLORS.secondaryColor} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: error ? "red !important" : `${COLORS.secondaryColor} !important`,
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: `${fontFamilyAll} !important`,
    color: error ? "red !important" : `${COLORS.secondaryColor} !important`,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: error ? "red !important" : `${COLORS.secondaryColor} !important`,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: `${fontFamilyAll} !important`,
    color: error ? "red !important" : `${COLORS.secondaryColor} !important`,
  },
});

const ContactForm = ({ details, formData, headingText, subHeadingText, buttonText }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const buildInitialValues = () =>
    formData.reduce((acc, field) => {
      let defaultVal = field.defaultValue;
      if (defaultVal === undefined) {
        if (field.type === "checkbox") defaultVal = false;
        else if (field.type === "checkboxGroup") defaultVal = [];
        else defaultVal = "";
      }
      const result = { ...acc, [field.id]: defaultVal };

      // Every "tel" field gets a companion `${id}Code` value that stores the
      // selected dialing code (defaults to India unless overridden).
      if (field.type === "tel") {
        result[`${field.id}Code`] = field.defaultCountryCode || "91";
      }

      return result;
    }, {});

  const [formValues, setFormValues] = useState(buildInitialValues());
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ---------- Validation ----------
  const validateField = (field, value) => {
    const { id, label, type, required = true } = field;
    const isEmpty =
      type === "checkboxGroup" ? !Array.isArray(value) || value.length === 0 : type === "checkbox" ? !value : !value;

    if (required && isEmpty) {
      if (type === "checkboxGroup") return `Select at least one ${label.toLowerCase()}`;
      if (type === "checkbox") return `You must provide ${label.toLowerCase()}`;
      return `${label} is required`;
    }

    if (!value && !required) return "";

    if (type === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Enter a valid email address";
    }

    if (type === "tel" && value && !/^\d{6,14}$/.test(value)) {
      return "Enter a valid phone number";
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    formData.forEach((field) => {
      const error = validateField(field, formValues[field.id]);
      if (error) newErrors[field.id] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------- Change handlers ----------
  const handleChange = (e, field) => {
    const { name, value, type, checked } = e.target;

    let newValue;
    if (field.type === "checkboxGroup") {
      const current = formValues[name] || [];
      newValue = checked ? [...current, value] : current.filter((v) => v !== value);
    } else if (field.type === "checkbox" || type === "checkbox") {
      newValue = checked;
    } else if (field.type === "tel") {
      // digits only
      newValue = value.replace(/\D/g, "");
    } else {
      newValue = value;
    }

    setFormValues((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: validateField(field, newValue) }));
  };

  const handleCountryCodeChange = (field, code) => {
    setFormValues((prev) => ({ ...prev, [`${field.id}Code`]: code }));
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const payload = {
        data: {
          title: formValues.title,
          firstname: formValues.firstName,
          lastname: formValues.lastName,
          email: formValues.email,
          mobile: formValues.mobile ? `${formValues.mobileCode}${formValues.mobile}` : null,
          phone: formValues.altMobile ? `${formValues.altMobileCode}${formValues.altMobile}` : null,
          companyname: formValues.company,
          preferredcommunicationmethod: formValues.communication.join(";"),
          consentprovided: formValues.consent,
          origin: "Website",
        },
      };
      await submitFormAPI(payload);

      setSnackbarMessage("Thank you! Your form has been submitted successfully.");
      setSnackbarOpen(true);

      setFormValues(buildInitialValues());
      setErrors({});
    } catch (error) {
      console.error("Form submission error:", error);
      setSnackbarMessage("Failed to submit. Please try again later.");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- Field renderers ----------
  const renderLabel = (field) => (
    <FormLabel
      htmlFor={field.id}
      sx={{
        mb: 1,
        fontWeight: 500,
        color: errors[field.id] ? "red" : COLORS.secondaryColor,
        fontFamily: `${fontFamilyAll} !important`,
        fontSize: { xs: "0.9rem", sm: "1rem" },
      }}
    >
      {field.label} {field.required !== false && "*"}
    </FormLabel>
  );

  const renderField = (field) => {
    switch (field.type) {
      case "select":
        return (
          <FormControl fullWidth margin="normal" key={field.id}>
            {renderLabel(field)}
            <TextField
              select
              id={field.id}
              name={field.id}
              value={formValues[field.id]}
              onChange={(e) => handleChange(e, field)}
              sx={FieldStyles(!!errors[field.id])}
              error={!!errors[field.id]}
              helperText={errors[field.id]}
              size={isMobile ? "small" : "medium"}
            >
              {(field.options || []).map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        );

      case "checkboxGroup":
        return (
          <FormControl
            component="fieldset"
            error={!!errors[field.id]}
            margin="normal"
            fullWidth
            key={field.id}
          >
            <FormLabel
              component="legend"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: errors[field.id] ? "red" : COLORS.secondaryColor,
                fontFamily: `${fontFamilyAll} !important`,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {field.label} {field.required !== false && "*"}
            </FormLabel>
            <FormGroup
              sx={{
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {(field.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      checked={(formValues[field.id] || []).includes(opt)}
                      onChange={(e) => handleChange(e, field)}
                      name={field.id}
                      value={opt}
                      sx={{
                        color: COLORS.secondaryColor,
                        "&.Mui-checked": { color: COLORS.secondaryColor },
                      }}
                    />
                  }
                  label={
                    <Typography fontFamily={fontFamilyAll} fontSize={{ xs: "0.85rem", sm: "1rem" }}>
                      {opt}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
            {errors[field.id] && (
              <FormHelperText sx={{ color: "red", fontFamily: fontFamilyAll }}>
                {errors[field.id]}
              </FormHelperText>
            )}
          </FormControl>
        );

      case "checkbox":
        return (
          <Box mt={2} mb={1} key={field.id}>
            <FormControl error={!!errors[field.id]}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formValues[field.id]}
                    onChange={(e) => handleChange(e, field)}
                    name={field.id}
                    sx={{
                      color: COLORS.secondaryColor,
                      "&.Mui-checked": { color: COLORS.secondaryColor },
                    }}
                  />
                }
                label={
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: fontFamilyAll,
                      color: errors[field.id] ? "red" : COLORS.secondaryColor,
                      fontWeight: 500,
                      fontSize: { xs: "0.85rem", sm: "1rem" },
                    }}
                  >
                    {field.label} {field.required !== false && "*"}
                  </Typography>
                }
              />
              {errors[field.id] && (
                <FormHelperText sx={{ color: "red", fontFamily: fontFamilyAll }}>
                  {errors[field.id]}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case "tel":
        return (
          <FormControl fullWidth margin="normal" key={field.id}>
            {renderLabel(field)}
            <TextField
              id={field.id}
              name={field.id}
              type="tel"
              variant="outlined"
              placeholder={field.placeholder}
              size={isMobile ? "small" : "medium"}
              sx={FieldStyles(!!errors[field.id])}
              value={formValues[field.id]}
              onChange={(e) => handleChange(e, field)}
              error={!!errors[field.id]}
              helperText={errors[field.id] || field.helperText}
              inputProps={{ inputMode: "numeric", maxLength: field.maxLength || 12 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    <Select
                      variant="standard"
                      disableUnderline
                      value={formValues[`${field.id}Code`] || "91"}
                      onChange={(e) => handleCountryCodeChange(field, e.target.value)}
                      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                      // Collapsed field only ever shows "+<code>"
                      renderValue={(selected) => `+${selected}`}
                      sx={{
                        fontFamily: fontFamilyAll,
                        color: COLORS.secondaryColor,
                        fontWeight: 500,
                        "& .MuiSelect-select": {
                          py: 0,
                          pr: "20px !important",
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      {countryCodes.map((c) => (
                        <MenuItem key={c.code} value={c.code}>
                          +{c.code} {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <Box
                      sx={{
                        borderLeft: `1px solid ${COLORS.secondaryColor}55`,
                        height: 22,
                        ml: 1.25,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        );

      default:
        // text | email
        return (
          <FormControl fullWidth margin="normal" key={field.id}>
            {renderLabel(field)}
            <TextField
              id={field.id}
              name={field.id}
              type={field.type}
              variant="outlined"
              placeholder={field.placeholder}
              multiline={field.multiline || false}
              rows={field.rows || 1}
              size={isMobile ? "small" : "medium"}
              sx={FieldStyles(!!errors[field.id])}
              value={formValues[field.id]}
              onChange={(e) => handleChange(e, field)}
              error={!!errors[field.id]}
              helperText={errors[field.id] || field.helperText}
            />
          </FormControl>
        );
    }
  };

  // Group consecutive fields that share the same `row` key so they render
  // side-by-side (e.g. Title + First Name + Last Name in one row) on larger
  // screens, and stack on mobile.
  const groupedFields = [];
  formData.forEach((field) => {
    const lastGroup = groupedFields[groupedFields.length - 1];
    if (field.row && lastGroup && lastGroup.row === field.row) {
      lastGroup.fields.push(field);
    } else {
      groupedFields.push({ row: field.row || null, fields: [field] });
    }
  });

  const requiresConsent = formData.some((f) => f.type === "checkbox" && f.required !== false);
  const consentField = formData.find((f) => f.type === "checkbox");
  const isSubmitDisabled = isLoading || (requiresConsent && consentField && !formValues[consentField.id]);

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 0 } }}>
      <ToastMessages
        isOpen={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={snackbarMessage}
        severity={snackbarMessage.includes("Thank you") ? "success" : ""}
      />

      <Box mb={{ xs: 3, md: 4 }} textAlign="center">
        <Typography
          variant="h4"
          fontWeight="bold"
          color={COLORS.secondaryColor}
          gutterBottom
          fontFamily={fontFamilyAll}
          sx={{ fontSize: { xs: "1.6rem", sm: "2rem", md: "2.125rem" } }}
        >
          {headingText}
        </Typography>
        <Typography
          variant="subtitle1"
          color={COLORS.primaryColor}
          fontFamily={fontFamilyAll}
          sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
        >
          {subHeadingText}
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
        <Grid item xs={12} md={6} mb={{ xs: 2, md: 4 }}>
          <Card
            sx={{
              p: { xs: 2, sm: 3 },
              height: { xs: "auto", md: "90%" },
              borderRadius: "16px",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
              border: `1px solid ${COLORS.secondaryColor}30`,
            }}
          >
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
              <form onSubmit={handleSubmit}>
                {groupedFields.map((group, idx) =>
                  group.fields.length > 1 ? (
                    <Box
                      key={group.row || idx}
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={{ xs: 0, sm: 2 }}
                      flexWrap="wrap"
                    >
                      {group.fields.map((field) => (
                        <Box key={field.id} sx={{ flex: field.flex || 1, minWidth: { xs: "100%", sm: 120 } }}>
                          {renderField(field)}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    renderField(group.fields[0])
                  )
                )}

                <Box display="flex" justifyContent={{ xs: "stretch", sm: "flex-start" }} mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth={isMobile}
                    disabled={isSubmitDisabled}
                    sx={{
                      fontFamily: `${fontFamilyAll} !important`,
                      borderRadius: "20px",
                      backgroundColor: COLORS.secondaryColor,
                      textTransform: "none",
                      px: 4,
                      py: { xs: 1.25, sm: 1 },
                      minWidth: { xs: "100%", sm: 160 },
                    }}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        Submitting...
                      </>
                    ) : (
                      buttonText
                    )}
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            mt={{ xs: 0, md: 4 }}
            sx={{
              position: "relative",
              width: "100%",
              // Responsive aspect ratio instead of a fixed 600px height,
              // so the map scales sensibly on any screen width.
              pt: { xs: "75%", sm: "60%", md: "100%", lg: "85%" },
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Box
              component="iframe"
              title="Google Map"
              src={googleMapLink}
              allowFullScreen
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactForm;